import { useCallback, useMemo, useState } from 'react'

import i18n from 'modules/core/lib/i18n'
import appStore from 'modules/core/store/models/app'

import { useDeleteWorkAreaMapSpaceLocation } from 'modules/space_control/hooks/useDeleteWorkAreaMapSpaceLocation'
import { useUpsertWorkAreaMapSpaceLocation } from 'modules/space_control/hooks/useUpsertWorkAreaMapSpaceLocation'
import { WorkAreaMapLocationNode } from 'modules/space_control/hooks/useWorkAreaMap'
import { WorkSpaceNode } from 'modules/space_control/hooks/useWorkSpaces'

export const SETTINGS_EVENT = {
  PIN_PLACED: 'settings:pinPlaced',
  PIN_DELETED: 'settings:pinDeleted',
  PIN_HOVERED: 'settings:pinHovered',
} as const

export type SettingsEventType = (typeof SETTINGS_EVENT)[keyof typeof SETTINGS_EVENT]

interface UseSettingsMapEventsProps {
  selectedWorkSpace: WorkSpaceNode | null
  mapId?: number
  onEvent?: (
    event: SettingsEventType,
    payload?: { workSpaceLocation: WorkAreaMapLocationNode | null }
  ) => void
}

export const useSettingsMapEvents = ({
  selectedWorkSpace,
  mapId,
  onEvent,
}: UseSettingsMapEventsProps) => {
  const { upsertWorkAreaMapSpaceLocation, loading: isPlacingPin } =
    useUpsertWorkAreaMapSpaceLocation()
  const { deleteWorkAreaMapSpaceLocation } = useDeleteWorkAreaMapSpaceLocation()
  const [alertWorkSpace, setAlertWorkSpace] = useState<WorkAreaMapLocationNode | null>(null)
  const [isDeletingPin, setIsDeletingPin] = useState(false)

  const handleMapClick = useCallback(
    async (coordinates: { x: number; y: number }) => {
      setAlertWorkSpace(null)
      if (!selectedWorkSpace?.id || !mapId) return

      try {
        await upsertWorkAreaMapSpaceLocation({
          workAreaMapId: mapId,
          workSpaceId: selectedWorkSpace.id,
          x: coordinates.x,
          y: coordinates.y,
        })

        appStore.setFlash({
          type: 'correct',
          message: i18n.t('space_control.map.pin_placed_successfully', {
            workspaceName: selectedWorkSpace.name,
          }),
        })

        onEvent?.(SETTINGS_EVENT.PIN_PLACED)
      } catch (error) {
        appStore.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }
    },
    [selectedWorkSpace, mapId, upsertWorkAreaMapSpaceLocation, onEvent]
  )

  const handlePinClick = useMemo(
    () =>
      ({ workAreaMapLocation }: { workAreaMapLocation: WorkAreaMapLocationNode | null }) => {
        if (!workAreaMapLocation) return

        setAlertWorkSpace(workAreaMapLocation)
      },
    []
  )

  const handleDelete = useCallback(async () => {
    if (!alertWorkSpace?.id) return

    setIsDeletingPin(true)
    try {
      await deleteWorkAreaMapSpaceLocation({ id: alertWorkSpace.id })
      setAlertWorkSpace(null)
      onEvent?.(SETTINGS_EVENT.PIN_DELETED)
    } finally {
      setIsDeletingPin(false)
    }
  }, [alertWorkSpace, deleteWorkAreaMapSpaceLocation, onEvent])

  const dismissAlert = useCallback(() => {
    setAlertWorkSpace(null)
  }, [])

  const handleHover = useCallback(
    ({ workAreaMapLocation }: { workAreaMapLocation: WorkAreaMapLocationNode | null }) => {
      onEvent?.(SETTINGS_EVENT.PIN_HOVERED, { workSpaceLocation: workAreaMapLocation })
    },
    [onEvent]
  )

  const actions = useMemo(
    () => ({
      onPinClick: handlePinClick,
      onMapClick: handleMapClick,
      onPinHover: handleHover,
    }),
    [handlePinClick, handleMapClick, handleHover]
  )

  return {
    actions,
    isPlacingPin,
    isDeletingPin,
    alertWorkSpace,
    handleDelete,
    dismissAlert,
  }
}
